import { writable, get } from 'svelte/store';
import { writable as storedWritable } from '../stores/_localStore.js';

import { v4 as uuidv4 } from 'uuid';
import gql from 'graphql-tag';
import { client } from 'utils/apollo.js';

export const shoppingCartId = storedWritable('shoppingCartId', null);

// UTILITY FUNCTIONS
function calculateCartSubTotal(cartItems) {
  let total = 0;
  cartItems.forEach((item) => {
    total += item.totalPrice;
  });
  return total;
}

function countAllPieces(cartItems) {
  let count = 0;
  cartItems.forEach((item) => {
    count += item.itemSummary.top.count;
    count += item.itemSummary.bottom.count;
    count += item.itemSummary.hat.count;
    count += item.itemSummary.socks.count;
    count += item.itemSummary.accessory.count;
  });
  return count;
}

function calculateBulkyShipping(cartItems) {
  let isBulkyShipping = false;
  cartItems.forEach((item) => {
    if (item.itemSummary.top.isBulkyProduct && item.itemSummary.top.count > 1) {
      isBulkyShipping = true;
    }
    if (item.itemSummary.bottom.isBulkyProduct && item.itemSummary.bottom.count > 1) {
      isBulkyShipping = true;
    }
    if (item.itemSummary.hat.isBulkyProduct && item.itemSummary.hat.count > 1) {
      isBulkyShipping = true;
    }
    if (item.itemSummary.socks.isBulkyProduct && item.itemSummary.socks.count > 1) {
      isBulkyShipping = true;
    }
    if (item.itemSummary.accessory.isBulkyProduct && item.itemSummary.accessory.count > 1) {
      isBulkyShipping = true;
    }
  });
  return isBulkyShipping;
}

function calculateShippingPrice(option) {
  const count = countAllPieces(get(_cartDetails).items);
  const total = calculateCartSubTotal(get(_cartDetails).items);

  const isBulkyShipping = calculateBulkyShipping(get(_cartDetails).items);
  let price;

  if (option === 'auspost-parcel') {
    if (total < 60000) {
      if (count === 1) {
        price = 995;
      } else if (count > 10) {
        price = isBulkyShipping ? 4995 : 2995;
      } else if (count > 5) {
        price = isBulkyShipping ? 3495 : 1995;
      } else {
        price = isBulkyShipping ? 2495 : 1495;
      }
    } else {
      price = 0;
    }
  } else if (option === 'auspost-express') {
    if (count > 15) {
      price = isBulkyShipping ? 8995 : 4995;
    } else if (count > 10) {
      price = isBulkyShipping ? 6995 : 3995;
    } else if (count > 5) {
      price = isBulkyShipping ? 4995 : 2995;
    } else if (count > 1) {
      price = isBulkyShipping ? 3995 : 1995;
    } else {
      price = 1495;
    }
  }

  return price;
}

function calculateProductionPrice(option) {
  const total = calculateCartSubTotal(get(_cartDetails).items);
  let price;
  if (option === 'standard' || option === 'sublimation' || option === 'leavers') {
    price = 0;
  } else if (option === 'rush') {
    price = total * 0.25;
  } else if (option === 'lightning') {
    price = total * 0.8;
  }
  return Math.floor(price);
}

function calculateShippingAndProductionOptionsAndPricing() {
  const cart = get(_cartDetails);

  // — costs —
  const standardShippingCost = calculateShippingPrice('auspost-parcel');
  const expressShippingCost = calculateShippingPrice('auspost-express');
  const standardProductionCost = calculateProductionPrice('standard');
  const rushProductionCost = calculateProductionPrice('rush');
  const lightningProductionCost = calculateProductionPrice('lightning');
  const sublimationProductionCost = calculateProductionPrice('sublimation');
  const leaversProductionCost = calculateProductionPrice('leavers');

  // — availability & times arrays —
  let rushOk = true,
    lightningOk = true;
  const standardTimes = [];
  const rushTimes = [];
  const lightningTimes = [];
  const sublimationTimes = [];
  const leaversTimes = [];

  cart.items.forEach((item) => {
    const parts = [
      item.itemSummary.top,
      item.itemSummary.bottom,
      item.itemSummary.hat,
      item.itemSummary.socks,
      item.itemSummary.accessory,
    ].filter((p) => p?.count > 0);

    parts.forEach((p) => {
      // standard (fallback to sublimation)
      let std =
        p.deliveryTimes.find((t) => t.DeliveryTime.name === 'standard') ||
        p.deliveryTimes.find((t) => t.DeliveryTime.name === 'sublimation');
      if (std?.DeliveryTime.deliveryTime != null) standardTimes.push(std.DeliveryTime.deliveryTime);

      // rush
      const r = p.deliveryTimes.find((t) => t.DeliveryTime.name === 'rush');
      if (!r) rushOk = false;
      else if (r.DeliveryTime.deliveryTime != null) rushTimes.push(r.DeliveryTime.deliveryTime);

      // lightning
      const l = p.deliveryTimes.find((t) => t.DeliveryTime.name === 'lightning');
      if (!l) lightningOk = false;
      else if (l.DeliveryTime.deliveryTime != null) lightningTimes.push(l.DeliveryTime.deliveryTime);

      // sublimation & leavers
      const sub = p.deliveryTimes.find((t) => t.DeliveryTime.name === 'sublimation');
      if (sub?.DeliveryTime.deliveryTime != null) sublimationTimes.push(sub.DeliveryTime.deliveryTime);

      const lea = p.deliveryTimes.find((t) => t.DeliveryTime.name === 'leavers');
      if (lea?.DeliveryTime.deliveryTime != null) leaversTimes.push(lea.DeliveryTime.deliveryTime);
    });
  });

  // — maxima —
  const standardTime = standardTimes.length ? Math.max(...standardTimes) : undefined;
  const rushTime = rushOk && rushTimes.length ? Math.max(...rushTimes) : undefined;
  const lightningTime = lightningOk && lightningTimes.length ? Math.max(...lightningTimes) : undefined;
  const sublimationTime = sublimationTimes.length ? Math.max(...sublimationTimes) : undefined;
  const leaversTime = leaversTimes.length ? Math.max(...leaversTimes) : undefined;

  // — pick primary production option —
  const prodCandidates = [];
  if (leaversTime != null) prodCandidates.push({ key: 'leavers', time: leaversTime });
  if (sublimationTime != null) prodCandidates.push({ key: 'sublimation', time: sublimationTime });
  if (prodCandidates.length === 0 && standardTime != null) prodCandidates.push({ key: 'standard', time: standardTime });

  const primaryProduction = prodCandidates.length
    ? prodCandidates.reduce((a, b) => (b.time > a.time ? b : a)).key
    : undefined;

  return {
    // shipping costs
    standardShippingCost,
    expressShippingCost,
    // production costs
    standardProductionCost,
    rushProductionCost,
    lightningProductionCost,
    sublimationProductionCost,
    leaversProductionCost,
    // times & availability
    standardTime,
    rushTime,
    lightningTime,
    sublimationTime,
    leaversTime,
    rushAvailable: rushOk,
    lightningAvailable: lightningOk,
    // primary key
    primaryProduction,
  };
}

// CART DETAILS STORE
const _cartDetails = writable({
  id: get(shoppingCartId),
  total: 0,
  totalWithShipping: 0,
  totalWithShippingAndCoupon: 0,
  items: [],
  coupon: {
    code: null,
    isPercentage: false,
    includeShipping: false,
    value: 0,
    applyToShipping: false,
    isFreeShipping: false,
    isSingleUse: true,
  },
  shipping: {
    shippingSelection: 'auspost-parcel',
    price: 0,
  },
  production: {
    productionSelection: 'standard',
    price: 0,
  },
  updated: null,
  isInitialised: false,
  shippingAndProductionOptions: {
    standardShippingCost: 0,
    expressShippingCost: 0,
    standardProductionCost: null,
    rushProductionCost: 0,
    lightningProductionCost: 0,
    sublimationProductionCost: 0,
    leaversProductionCost: 0,
    standardTime: null,
    rushTime: null,
    lightningTime: null,
    sublimationTime: null,
    leaversTime: null,
    rushAvailable: false,
    lightningAvailable: false,
    primaryProduction: 'standard',
  },
});

async function validateCoupon(couponCode) {
  const response = await client.query({
    query: gql`
      query MyQuery($couponCode: String!) {
        CouponCode(where: { code: { _eq: $couponCode } }) {
          code
          expiryDate
          applyToShipping
          isFreeShipping
          isPercentage
          isSingleUse
          value
        }
      }
    `,
    variables: {
      couponCode,
    },
  });
  if (response.data.CouponCode.length > 0 && response.data.CouponCode[0].expiryDate > new Date()) {
    return response.data.Coupon[0];
  } else {
    return null;
  }
}

async function addCouponToCart(couponCode) {
  const coupon = await validateCoupon(couponCode);
  if (coupon) {
    _cartDetails.update((x) => {
      x.coupon = coupon;
      return x;
    });
    // Now update the total
    updateTotalShippingProduction(
      get(_cartDetails),
      get(_cartDetails).shipping.shippingSelection,
      get(_cartDetails).production.productionSelection,
    );
  }
}

function updateTotalShippingProduction(storeValue, shippingSelection, productionSelection) {
  const total = calculateCartSubTotal(storeValue.items);

  _cartDetails.update((x) => {
    x.total = total;
    return x;
  });

  const opts = calculateShippingAndProductionOptionsAndPricing();
  _cartDetails.update((x) => {
    x.shippingAndProductionOptions = opts;
    return x;
  });

  let shippingPrice;
  let productionPrice;

  // Determine shipping price
  if (!shippingSelection || shippingSelection === 'auspost-parcel') {
    shippingSelection = 'auspost-parcel';
    shippingPrice = calculateShippingPrice('auspost-parcel');
  } else if (shippingSelection === 'auspost-express') {
    shippingPrice = calculateShippingPrice('auspost-express');
  }

  _cartDetails.update((x) => {
    x.shipping = {
      price: shippingPrice,
      selection: shippingSelection,
    };
    return x;
  });

  // Determine production settings
  // If no production option is selected, use the primary production option
  // No option is selected when first adding to cart
  if (!productionSelection) {
    productionSelection = opts.primaryProduction;
    productionPrice = calculateProductionPrice(opts.primaryProduction);
  }
  if (
    (productionSelection === 'rush' && !opts.rushAvailable) ||
    (productionSelection === 'lightning' && !opts.lightningAvailable)
  ) {
    productionSelection = opts.primaryProduction;
    productionPrice = calculateProductionPrice(opts.primaryProduction);
  } else {
    productionPrice = calculateProductionPrice(productionSelection);
  }

  _cartDetails.update((x) => {
    x.production = {
      price: productionPrice,
      selection: productionSelection,
    };
    return x;
  });

  // Determing total
  const totalWithShipping = total + shippingPrice + productionPrice;
  _cartDetails.update((x) => {
    x.totalWithShipping = totalWithShipping;
    return x;
  });

  let totalWithShippingAndCoupon = totalWithShipping;
  if (storeValue.coupon?.isPercentage) {
    totalWithShippingAndCoupon = Math.round((totalWithShipping * (100 - storeValue.coupon.value)) / 100);
  } else {
    totalWithShippingAndCoupon = totalWithShipping - storeValue.coupon.value;
  }
  if (totalWithShippingAndCoupon < 0) {
    totalWithShippingAndCoupon = 0;
  }
  _cartDetails.update((x) => {
    x.totalWithShippingAndCoupon = totalWithShippingAndCoupon;
    return x;
  });
}

async function createShoppingCart() {
  const identifier = uuidv4();

  const response = await client.mutate({
    mutation: gql`
      mutation MyMutation($identifier: uuid!) {
        insert_ShoppingCart_one(object: { identifier: $identifier }) {
          identifier
          updated_at
        }
      }
    `,
    variables: {
      identifier,
    },
  });
  if (response.data && response.data.insert_ShoppingCart_one.identifier === identifier) {
    // Set the value of the id in the stored writable
    shoppingCartId.set(response.data.insert_ShoppingCart_one.identifier);

    // Update values in the in memory cart store.
    _cartDetails.update((x) => {
      (x.id = response.data.insert_ShoppingCart_one.identifier),
        (x.updated = response.data.insert_ShoppingCart_one.updated_at);
      x.isInitialised = true;
      return x;
    });
  } else if (response.errors) {
    console.log(response.errors);
  }
}

// Add item to cart (an item is one design - can include top, shorts, etc. which are added as designItems)
async function addItemToCart(
  shoppingCartIdentifier,
  customerNote,
  totalPrice,
  designState,
  cartPreviewImage,
  itemSummary,
  teamDetails,
) {
  /// /console.log("addItemToCart: ", shoppingCartIdentifier, customerNote, totalPrice, designState, cartPreviewImage, itemSummary, teamDetails);

  const response = await client.mutate({
    mutation: gql`
      mutation MyMutation(
        $customerNote: String!
        $shoppingCartIdentifier: uuid!
        $totalPrice: Int!
        $designState: jsonb!
        $cartPreviewImage: String!
        $itemSummary: jsonb!
        $teamDetails: jsonb!
      ) {
        insert_CartItem_one(
          object: {
            customerNote: $customerNote
            shoppingCartIdentifier: $shoppingCartIdentifier
            totalPrice: $totalPrice
            designState: $designState
            cartPreviewImage: $cartPreviewImage
            itemSummary: $itemSummary
            teamDetails: $teamDetails
          }
        ) {
          shoppingCartIdentifier
          id
        }
      }
    `,
    variables: {
      shoppingCartIdentifier,
      customerNote,
      totalPrice,
      designState,
      cartPreviewImage,
      itemSummary,
      teamDetails,
    },
  });

  if (response.data.insert_CartItem_one.shoppingCartIdentifier === shoppingCartIdentifier) {
    _cartDetails.update((x) => {
      x.items.push({
        id: response.data.insert_CartItem_one.id,
        shoppingCartIdentifier,
        customerNote,
        totalPrice,
        cartPreviewImage,
        itemSummary,
      });
      return x;
    });

    const storeValue = get(_cartDetails);
    updateTotalShippingProduction(storeValue, storeValue.shipping.selection, storeValue.production.selection);

    // return id here as it is used to update the design elements
    return response.data.insert_CartItem_one.id;
  } else {
    // error
  }
}

async function updateItemInCart(
  cartItemId,
  shoppingCartIdentifier,
  customerNote,
  totalPrice,
  designState,
  cartPreviewImage,
  itemSummary,
  teamDetails,
) {
  let storeValue = get(_cartDetails);

  const response = await client.mutate({
    mutation: gql`
      mutation MyMutation(
        $id: Int!
        $cartPreviewImage: String!
        $customerNote: String
        $designState: jsonb!
        $itemSummary: jsonb!
        $teamDetails: jsonb!
        $totalPrice: Int!
      ) {
        update_CartItem_by_pk(
          pk_columns: { id: $id }
          _set: {
            cartPreviewImage: $cartPreviewImage
            customerNote: $customerNote
            designState: $designState
            itemSummary: $itemSummary
            teamDetails: $teamDetails
            totalPrice: $totalPrice
          }
        ) {
          shoppingCartIdentifier
          id
        }
      }
    `,
    variables: {
      id: cartItemId,
      customerNote,
      totalPrice,
      designState,
      cartPreviewImage,
      itemSummary,
      teamDetails,
    },
  });

  if (response.data.update_CartItem_by_pk.shoppingCartIdentifier === shoppingCartIdentifier) {
    // Update the local state
    const itemIndex = storeValue.items.findIndex((x) => x.id === cartItemId);
    if (itemIndex === -1) {
      return 'error - item not found';
    }
    _cartDetails.update((x) => {
      (x.items[itemIndex].customerNote = customerNote),
        (x.items[itemIndex].totalPrice = totalPrice),
        (x.items[itemIndex].cartPreviewImage = cartPreviewImage),
        (x.items[itemIndex].itemSummary = itemSummary);
      return x;
    });

    // Update the total shipping and production price
    storeValue = get(_cartDetails);
    updateTotalShippingProduction(storeValue, storeValue.shipping.selection, storeValue.production.selection);

    // return id here as it is used to update the design elements
    return response.data.update_CartItem_by_pk.id;
  } else {
    console.log('addItemToCart error: ', shoppingCartIdentifier);
  }
}

// Add each design component to cart - e.g. custom tops, custom bottoms, etc.
// This is not reflected in the store - only in the database
async function addDesignItemToCartItem(designDataArray) {
  const response = await client.mutate({
    mutation: gql`
      mutation ($objects: [DesignItem_insert_input!]!) {
        insert_DesignItem(objects: $objects) {
          affected_rows
        }
      }
    `,
    variables: {
      objects: designDataArray,
    },
  });
  if (response.data.insert_DesignItem.affected_rows === designDataArray.length) {
    return response.data.insert_DesignItem.affected_rows;
  } else {
    console.log('addDesignItemToCartItem error: ', response.data);
  }
}

async function loadCart(shoppingCartIdentifier, reorder = false) {
  const response = await client.query({
    query: gql`
      query MyQuery($shoppingCartIdentifier: uuid!, $reorder: Boolean!) {
        ShoppingCart(where: { identifier: { _eq: $shoppingCartIdentifier }, checkedOut: { _eq: $reorder } }) {
          productionTime
          productionPrice
          identifier
          shippingPrice
          shippingSelection
          CartItems {
            id
            identifier
            itemSummary
            cartPreviewImage
            totalPrice
          }
        }
      }
    `,
    variables: {
      shoppingCartIdentifier,
      reorder,
    },
  });

  if (response.data.ShoppingCart.length > 0 && response.data.ShoppingCart[0].identifier === shoppingCartIdentifier) {
    _cartDetails.update((x) => {
      x.items = response.data.ShoppingCart[0].CartItems;
      return x;
    });

    const storeValue = get(_cartDetails); // Must get this after items have been added above
    const shippingPrice = response.data.ShoppingCart[0].shippingPrice;
    const shippingSelection = response.data.ShoppingCart[0].shippingSelection;
    const productionPrice = response.data.ShoppingCart[0].productionPrice;
    const productionSelection = response.data.ShoppingCart[0].productionTime;
    updateTotalShippingProduction(storeValue, shippingSelection, productionSelection);

    // set initialised to true
    _cartDetails.update((x) => {
      x.isInitialised = true;
      return x;
    });
    return 'success';
  } else {
    return 'error';
  }
}

// shopping cart identifier is not necessary - but will add so users can't arbitrarily delete by id
// Must have the identifier and id
// Must use the allow list for this to have effect, otherwise someone could delete a cart they don't own
async function deleteItemFromCart(cartItemId) {
  const storeValue = get(_cartDetails);
  const shoppingCartIdentifier = storeValue.id;
  const response = await client.mutate({
    mutation: gql`
      mutation ($shoppingCartIdentifier: uuid!, $id: Int!) {
        delete_CartItem(where: { ShoppingCart: { identifier: { _eq: $shoppingCartIdentifier } }, id: { _eq: $id } }) {
          affected_rows
        }
      }
    `,
    variables: {
      id: cartItemId,
      shoppingCartIdentifier,
    },
  });

  if (response.data.delete_CartItem.affected_rows > 0) {
    // let originalStoreValue = get(_cartDetails)
    // let deletedItem = originalStoreValue.items.find(x => x.id === cartItemId)

    _cartDetails.update((x) => {
      x.items = x.items.filter((i) => i.id !== cartItemId);
      return x;
    });

    const storeValue = get(_cartDetails); // After the item has been removed
    updateTotalShippingProduction(storeValue, storeValue.shipping.selection, storeValue.production.selection);
    // return deletedItem
  } else {
    // console.log("deleteItemFromCart error: ", response.data.delete_CartItem);
    // return []
  }
}

async function deleteDesignItems(cartItemId) {
  const response = await client.mutate({
    mutation: gql`
      mutation MyMutation($cartItemId: Int!) {
        delete_DesignItem(where: { cartItemId: { _eq: $cartItemId } }) {
          affected_rows
        }
      }
    `,
    variables: {
      cartItemId,
    },
  });

  if (response.data.delete_DesignItem.affected_rows > 0) {
    return 'success';
  } else {
    // console.log("deleteDesignItems error: ", response.data.delete_DesignItem);
  }
}

function changeShippingOrProductionOption(productionOption, shippingOption) {
  _cartDetails.update((x) => {
    x.shipping.selection = shippingOption;
    x.production.selection = productionOption;
    return x;
  });
  const storeValue = get(_cartDetails);
  updateTotalShippingProduction(storeValue, shippingOption, productionOption);
}

async function setCheckedOutStatus(shoppingCartIdentifier, status) {
  const response = await client.mutate({
    mutation: gql`
      mutation ($_eq: uuid!, $checkedOut: Boolean!) {
        update_ShoppingCart(where: { identifier: { _eq: $_eq } }, _set: { checkedOut: $checkedOut }) {
          affected_rows
        }
      }
    `,
    variables: {
      _eq: shoppingCartIdentifier,
      checkedOut: status,
    },
  });
}

function createItemsForGoogleAnalytics() {
  const storeValue = get(_cartDetails);
  const items = [];
  // Note in cartDetails multiple product types exist as one item - e.g. top and shorts
  for (let i = 0; i < storeValue.items.length; i++) {
    const item = storeValue.items[i];
    const category = item.itemSummary.category;
    const collection = item.itemSummary.collection;
    // Now we can check each product type associated with one cart item
    if (item.itemSummary.top.count > 0) {
      items.push({
        id: item.id,
        name: item.itemSummary.top.name,
        price: (item.itemSummary.top.price / 100).toFixed(2),
        quantity: item.itemSummary.top.count,
        currency: 'AUD',
        item_category: category,
        item_category2: collection,
        item_category3: 'top',
        item_category4: item.itemSummary.top.isCustomised,
        item_variant: item.itemSummary.top.colour,
      });
    }
    if (item.itemSummary.bottom.count > 0) {
      items.push({
        id: item.id,
        name: item.itemSummary.bottom.name,
        price: (item.itemSummary.bottom.price / 100).toFixed(2),
        quantity: item.itemSummary.bottom.count,
        currency: 'AUD',
        item_category: category,
        item_category2: collection,
        item_category3: 'bottom',
        item_category4: item.itemSummary.bottom.isCustomised,
        item_variant: item.itemSummary.bottom.colour,
      });
    }
    if (item.itemSummary.hat.count > 0) {
      items.push({
        id: item.id,
        name: item.itemSummary.hat.name,
        price: (item.itemSummary.hat.price / 100).toFixed(2),
        quantity: item.itemSummary.hat.count,
        currency: 'AUD',
        item_category: category,
        item_category2: collection,
        item_category3: 'hat',
        item_category4: item.itemSummary.hat.isCustomised,
        item_variant: item.itemSummary.hat.colour,
      });
    }
    if (item.itemSummary.socks.count > 0) {
      items.push({
        id: item.id,
        name: item.itemSummary.socks.name,
        price: (item.itemSummary.socks.price / 100).toFixed(2),
        quantity: item.itemSummary.socks.count,
        currency: 'AUD',
        item_category: category,
        item_category2: collection,
        item_category3: 'socks',
        item_category4: item.itemSummary.socks.isCustomised,
        item_variant: item.itemSummary.socks.colour,
      });
    }
    if (item.itemSummary.accessory.count > 0) {
      items.push({
        id: item.id,
        name: item.itemSummary.accessory.name,
        price: (item.itemSummary.accessory.price / 100).toFixed(2),
        quantity: item.itemSummary.accessory.count,
        currency: 'AUD',
        item_category: category,
        item_category2: collection,
        item_category3: 'accessory',
        item_category4: item.itemSummary.accessory.isCustomised,
        item_variant: item.itemSummary.accessory.colour,
      });
    }
  }
  return items;
}

function saveAdTrackingData(shoppingCartIdentifier, adTrackingData) {
  const adTrackingDataWithCartId = {
    ...adTrackingData,
    shoppingCartIdentifier,
  };
  try {
    client.mutate({
      mutation: gql`
        mutation InsertAdTrackingMutation($object: adTracking_insert_input!) {
          insert_adTracking_one(object: $object) {
            shoppingCartIdentifier
          }
        }
      `,
      variables: {
        object: adTrackingDataWithCartId,
      },
    });
  } catch (error) {
    console.log('Error saving ad tracking data: ', error.message);
  }
}

export const cartDetails = {
  subscribe: _cartDetails.subscribe,
  set: _cartDetails.set,
  update: _cartDetails.update,
  createShoppingCart,
  loadCart,
  addItemToCart,
  addCouponToCart,
  addDesignItemToCartItem,
  deleteDesignItems,
  deleteItemFromCart,
  changeShippingOrProductionOption,
  calculateShippingPrice,
  calculateProductionPrice,
  setCheckedOutStatus,
  updateItemInCart,
  createItemsForGoogleAnalytics,
  saveAdTrackingData,
};
